import * as React from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Flex, Pagination, PaginationProps, Row, Select, Spin,
} from 'antd';
import styled from 'styled-components';
import { LoadingOutlined } from '@ant-design/icons';
import { DivMinWidth, FlexPadStyled } from '../App';
import data from '../membersData.json';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LogoBC } from '../components/LogoBC';
import { useResizeWidthWindow } from '../components/hook/useResizeWidthWindow';
import MemberCardItem from '../components/card/MemberCardItem';
import { MemberDataType } from '../../../common/types';

// const FlexStyled = styled(Flex)`
//   padding: 20px 40px;
//   align-items: stretch;
//
//   @media (max-width: 768px) {
//     padding: 20px;
//   }
// `;

const DivFilterStyled = styled.div`
  margin: 20px 0px;
  align-items: stretch;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FlexFilterStyled = styled(Flex)`
  padding: 20px;
  border: 1px solid rgba(115, 115, 115, 0.2);
  background-color: #FAFAFA;
  border-radius: 4px;
`;

const FlexPaginatorStyled = styled(Flex)`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 10px;
  }
`;

export const MemberListingPage: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string[]>([]);
  const [spin, setSpin] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [newPageSize, setNewPageSize] = useState(9);
  const startIndex = (currentPage - 1) * newPageSize;
  const endIndex = startIndex + newPageSize;

  const filteredMembers = useMemo(() => (Object.values(data) as MemberDataType[])
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((member) => {
      const matchCompany = selectedCompany.length === 0 || selectedCompany.includes(member.name);
      const matchType = selectedType.length === 0 || selectedType.includes(member.type);
      return matchCompany && matchType;
    }), [selectedCompany, selectedType]);

  const paginatedDataMembers = filteredMembers.slice(startIndex, endIndex).sort((a:any, b:any) => b - a);

  const selectCompany = Array.from(
    new Set(Object.values(data).map((item) => item.name)),
  )
    .filter((name): name is string => !!name && name.trim() !== '')
    .sort((a, b) => a.localeCompare(b));

  const optionsCompany = selectCompany.map((company) => ({
    label: company,
    value: company,
  }));

  const handleCompanyChange = (value: string[]) => {
    setSpin(true);
    setTimeout(() => setSpin(false), 500);
    setSelectedCompany(value);
  };

  const selectType = Array.from(
    new Set(Object.values(data).map((item) => item.type)),
  )
    .filter((name): name is string => !!name && name.trim() !== '');

  const optionsType = selectType.map((company) => ({
    label: company,
    value: company,
  }));

  const handleTypeChange = (value: string[]) => {
    setSpin(true);
    setTimeout(() => setSpin(false), 500);
    setSelectedType(value);
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
    setCurrentPage(current);
    setNewPageSize(pageSize);
  };

  const showTotal: PaginationProps['showTotal'] = (total) => `Total ${total} members`;

  return <>
        <Helmet>
            <title>ALL MEMBERS</title>
            <meta
                name="description"
                content="AWMAC - Architectural Woodwork Manufacturers Association of Canada AWARDS"
            />
            <link rel="canonical" href='https://awmacbc.com/member-listing'/>
        </Helmet>
        <DivMinWidth>
            <div style={{ padding: '20px' }}>
                <FlexPadStyled vertical>
                    <LogoBC />
                </FlexPadStyled>
                <Flex style={{ margin: '20px 0' }}>
                    <Breadcrumbs />
                </Flex>
                <h1 style={{ textAlign: 'center', fontFamily: 'var(----fnt-font-main)' }}>AWMAC BC MEMBERS</h1>
                <DivFilterStyled>
                    <FlexFilterStyled vertical gap={10}>
                        <Select
                            showSearch
                            mode="multiple"
                            size={'middle'}
                            placeholder="Select a company"
                            optionFilterProp="label"
                            onChange={handleCompanyChange}
                            options={optionsCompany}
                            style={{ width: '100%' }}
                        />
                        <Select
                            showSearch
                            mode="multiple"
                            size={'middle'}
                            placeholder="Select a type"
                            optionFilterProp="label"
                            onChange={handleTypeChange}
                            options={optionsType}
                            style={{ width: '100%' }}
                        />
                    </FlexFilterStyled>
                </DivFilterStyled>
                <Spin spinning={spin} style={{ minHeight: 400 }} indicator={<LoadingOutlined style={{ width: '100%', fontSize: 100 }}/>}>
                    {/* <FlexStyled justify={'space-between'} align={'center'} wrap={'wrap'} gap={20}> */}
                    <div>
                        <Row gutter={[24, 24]}>
                            {paginatedDataMembers.map((member) => <MemberCardItem key={member.id} dataCard={member}/>)}
                        </Row>
                    </div>
                    {/* </FlexStyled> */}
                    <FlexPaginatorStyled justify={'center'}>
                        <Pagination
                            showSizeChanger
                            onChange={onShowSizeChange}
                            onShowSizeChange={onShowSizeChange}
                            defaultCurrent={1}
                            total={filteredMembers.length}
                            showTotal={showTotal}
                            pageSizeOptions={[9, 18, 36]}
                            defaultPageSize={9}
                            size={useResizeWidthWindow() < 700 ? 'small' : 'default'}
                        />
                    </FlexPaginatorStyled>
                </Spin>
            </div>
        </DivMinWidth>
    </>;
};
